<template>
  <div class="page">
    <query-form label-width="90px">
      <template v-slot:formitem>
        <query-form-item :label="$t('common.brand')">
          <el-input clearable v-model="queryForm.boutiqueBrandName"></el-input>
        </query-form-item>
        <query-form-item :label="$t('common.designerId')">
          <el-input clearable v-model="queryForm.designerId"></el-input>
        </query-form-item>
        <query-form-item :label="$t('common.color')">
          <el-input clearable v-model="queryForm.colorCode"></el-input>
        </query-form-item>
        <query-form-item :label="$t('common.season')" label-width="110px">
          <el-input clearable v-model="queryForm.seasonCode"></el-input>
        </query-form-item>
        <query-form-item :label="$t('common.isCo')" v-if="showIsCo">
          <el-select v-model="queryForm.coFlag" clearable style="width: 100%">
            <el-option :label="$t('common.yes')" :value="1"></el-option>
            <el-option :label="$t('common.no')" :value="0"></el-option>
          </el-select>
        </query-form-item>
        <query-form-item :label="$t('common.stock')">
          <el-select clearable v-model="queryForm.stock" style="width: 100%">
            <el-option :label="$t('product.inStock')" value="1"></el-option>
            <el-option :label="$t('product.soldStock')" value="0"></el-option>
          </el-select>
        </query-form-item>
        <query-form-item :label="$t('product.errorCode')">
          <el-select clearable filterable v-model="queryForm.codeIds" style="width: 100%">
            <el-option v-for="item in errorCodeList" :key="item.code" :label="item.codeName" :value="item.code"></el-option>
          </el-select>
        </query-form-item>
        <query-form-item :label="$t('product.processingMark')">
          <el-select v-model="queryForm.draftTag" clearable style="width: 100%">
            <el-option :label="$t('common.yes')" :value="1"></el-option>
            <el-option :label="$t('common.no')" :value="0"></el-option>
          </el-select>
        </query-form-item>
        <query-form-item :label="$t('product.wholesaleNo')" v-if="showNewD">
          <el-input clearable v-model="queryForm.wholesaleNo"></el-input>
        </query-form-item>
      </template>
      <template>
        <el-button class="mb10" type="primary" @click="search">{{ $t('common.search') }}</el-button>
        <el-button class="mb20" type="info" @click="reset">{{ $t('common.reset') }}</el-button>
      </template>
    </query-form>

    <table-toolbar flex>
      <div>
        <el-button type="primary" :disabled="selections.length == 0" @click="handleBatchDelete(selections)">{{ $t('product.batchDelete') }}</el-button>
        <el-button type="primary" :disabled="selections.length == 0" @click="handleBatchProcess(selections)">{{ $t('product.batchProcess') }}</el-button>
      </div>
      <el-dropdown @command="handleExport">
        <el-button plain type="primary">{{ $t('product.export') }}<i class="el-icon-arrow-down"></i></el-button>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item command="comma">CSV (Comma)</el-dropdown-item>
          <el-dropdown-item command="semicolon">CSV (Semicolon)</el-dropdown-item>
          <el-dropdown-item command="excel">Excel</el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </table-toolbar>

    <el-table class="spacing-table" v-loading="loading" :data="tableData" @selection-change="tableSelectionChange" @sort-change="sortChange">
      <el-table-column type="selection" width="55"></el-table-column>
      <el-table-column :label="$t('common.productInfo')" width="100">
        <template slot-scope="scope">
          <list-img :img="scope.row.img" :width="80"></list-img>
        </template>
      </el-table-column>
      <el-table-column label="" min-width="130">
        <template slot-scope="scope">
          <div class="product-cell">
            <p v-if="showNewD">
              <span class="label">{{ $t('product.wholesaleNo') }}: </span>{{ scope.row.wholesaleNo }}
            </p>
            <p class="">{{ scope.row.brandName }}</p>
            <p class="mt10 keep-space">
              <span class="label">{{ $t('common.designerId') }}: </span>{{ scope.row.designerId }}
            </p>
            <p class="keep-space">
              <span class="label">{{ $t('common.color') }}: </span>{{ scope.row.colorCode }}
            </p>
            <div class="signet" v-if="scope.row.draftTag === 0">{{ $t('common.untreated') }}</div>
            <div class="signet light" v-if="scope.row.draftTag === 1">{{ $t('common.processed') }}</div>
          </div>
        </template>
      </el-table-column>
      <el-table-column :label="$t('common.season') + (showIsCo ? `(${$t('common.isCo')})` : '')">
        <template slot-scope="scope"> {{ scope.row.boutiqueSeasonCode }}{{ scope.row.coFlag === 1 ? '(CO)' : '' }} </template>
      </el-table-column>
      <el-table-column prop="categoryPath" :label="$t('common.category')" min-width="130"></el-table-column>
      <!-- <el-table-column prop="skuTotal" :label="$t('product.numberOfSizes')"></el-table-column> -->
      <el-table-column prop="stock" :label="$t('common.stock')"></el-table-column>
      <el-table-column :label="$t('product.errorCode')" min-width="150">
        <template slot-scope="scope">
          <template v-if="scope.row.errorCodeFold">
            <p>{{ scope.row.errorCode }}</p>
          </template>
          <template v-else>
            <p v-for="(item, index) in scope.row.errorCodeList" :key="index">{{ item }}</p>
          </template>
          <el-link v-if="scope.row.errorCount > 1 && scope.row.errorCodeFold" type="primary" @click="loadErrorCodeList(scope)"
            >{{ $t('product.all') }}({{ scope.row.errorCount }})</el-link
          >
          <el-link v-if="!scope.row.errorCodeFold" type="primary" @click="foldErrorCodeList(scope)">{{ $t('product.putAway') }}</el-link>
        </template>
      </el-table-column>
      <el-table-column :label="$t('product.similarProducts')" min-width="180">
        <template slot-scope="scope">
          <el-link v-if="scope.row.loadStatus === 0" type="primary" @click="loadSimilar(scope)">{{ $t('product.clickFindSimilar') }}</el-link>
          <p v-if="scope.row.loadStatus === 1"><i class="el-icon-loading"></i> {{ $t('common.loading') }}</p>
          <div v-if="scope.row.loadStatus === 2 && scope.row.similar" class="product-info">
            <img
              class="opacity50"
              :src="scope.row.similar.img + '?x-oss-process=image/auto-orient,1/resize,m_lfit,w_80/quality,q_100/format,jpg'"
              alt=""
            />
            <p>{{ scope.row.similar.brandName }}&nbsp;</p>
            <p class="keep-space">
              <span class="label">{{ $t('common.designerId') }}: </span>{{ scope.row.similar.designerId }}
            </p>
            <p class="keep-space">
              <span class="label">{{ $t('common.color') }}: </span>{{ scope.row.similar.colorCode }}
            </p>
            <el-link type="primary" @click="showDrawer(scope)">{{ $t('product.moreInfo') }}</el-link>
          </div>
          <p v-if="scope.row.loadStatus === 2 && !scope.row.similar">
            {{ $t('product.similarT1') }}<el-link type="primary" @click="openEdit(scope.row)">{{ $t('product.similarT2') }}</el-link>
          </p>
        </template>
      </el-table-column>
      <el-table-column :label="$t('product.createTime')" min-width="100">
        <template slot-scope="scope">
          {{ scope.row.createdTime | dateFormat('yyyy-MM-dd') }}<br />
          {{ scope.row.createdTime | dateFormat('HH:mm:ss') }}
        </template>
      </el-table-column>
      <el-table-column :label="$t('product.updateTime')" min-width="100">
        <template slot-scope="scope">
          {{ scope.row.updatedTime | dateFormat('yyyy-MM-dd') }}<br />
          {{ scope.row.updatedTime | dateFormat('HH:mm:ss') }}
        </template>
      </el-table-column>
      <el-table-column :label="$t('common.action')" width="150">
        <template slot-scope="scope">
          <el-link type="primary" @click="openEdit(scope.row)">{{ $t('common.edit') }}</el-link>
          <el-link class="ml10" type="primary" @click="handleBatchDelete([scope.row])">{{ $t('common.delete') }}</el-link>
          <el-link class="ml10" type="primary" @click="handleBatchProcess([scope.row])">{{ $t('common.process') }}</el-link>
        </template>
      </el-table-column>
    </el-table>
    <el-pagination
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
      :current-page="queryForm.pageNo"
      :page-sizes="[10, 25, 50, 100]"
      :page-size="queryForm.pageSize"
      layout="total, sizes, prev, pager, next, jumper"
      :total="total"
    >
    </el-pagination>

    <el-dialog :title="$t('product.exportTips')" :visible.sync="dialogVisible" width="400px">
      {{ $t('product.exportingCheck') }}
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">{{ $t('common.later') }}</el-button>
        <el-button type="primary" @click="$router.push({ path: '/product/export' })">{{ $t('product.goto') }}</el-button>
      </span>
    </el-dialog>

    <el-drawer :title="$t('product.similarProducts')" :visible.sync="drawer" size="840px">
      <div class="drawer-container">
        <div class="product-info">
          <img class="opacity50" :src="drawerSimilar.img + '?x-oss-process=image/auto-orient,1/resize,m_lfit,w_80/quality,q_100/format,jpg'" alt="" />
          <p class="f16">{{ drawerSimilar.brandName }}&nbsp;</p>
          <p class="f16 mt20 keep-space">
            <span class="label">{{ $t('common.designerId') }}: </span>{{ drawerSimilar.designerId }}
          </p>
          <p class="f16 keep-space">
            <span class="label">{{ $t('common.color') }}: </span>{{ drawerSimilar.colorCode }}
          </p>
        </div>
        <div class="product-list border-color">
          <div class="product-item border-color" v-for="item in drawerSimilarList" :key="item.spuId">
            <div class="product-imgs">
              <template v-for="index in 3">
                <img
                  class="opacity50"
                  :key="index"
                  :src="item.coverImg[index - 1] + '?x-oss-process=image/auto-orient,1/resize,m_lfit,w_140/quality,q_100/format,jpg'"
                  alt=""
                />
              </template>
            </div>
            <div class="product-desc">
              <p>
                <span class="label">{{ $t('common.brand') }}</span
                >: {{ item.brandName }}
              </p>
              <p class="keep-space">
                <span class="label">{{ $t('common.designerId') }}</span
                >: {{ item.designerId }}
              </p>
              <p class="keep-space">
                <span class="label">{{ $t('common.color') }}</span
                >: {{ item.colorCode }}
              </p>
              <p>
                <span class="label">{{ $t('common.category') }}</span
                >: {{ item.categoryAllName }}
              </p>
              <p>
                <span class="label">{{ $t('common.season') }}</span
                >: {{ item.seasonCode2 }}
              </p>
              <el-button type="primary" @click="similarCreateProduct(item)" size="mini">Same</el-button>
            </div>
          </div>
        </div>
      </div>
    </el-drawer>

    <el-dialog title="Same" :visible.sync="dialogVisibleSame" :close-on-click-modal="false" width="500px">
      <el-select v-model="wholesaleNo" style="width: 100%;margin-bottom:10px" :placeholder="$t('product.wholesaleNo')" v-if="vendorType == 7">
        <el-option v-for="item in codeList" :key="item.wholesaleNo" :label="item.wholesaleNo" :value="item.wholesaleNo"></el-option>
      </el-select>
       <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisibleSame = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" @click="similarCreateProductNew()">{{ $t('common.ok') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import {
  getDraftsList,
  deleteDraft,
  exportDraftsProduct,
  exportProductExcel,
  searchLikeProductList,
  similarCreateProduct,
  queryBoutiqueErrorByBoutiqueProductId,
  getErrorCodeList,
  draftsProductMarkTag,
  getWholesaleValidList
} from '../../api/product';
import ListImg from '../../components/ListImg';
import { getUserInfo } from "../../api/system"

export default {
  name: 'ProductExport',
  components: {
    ListImg,
  },
  data() {
    return {
      loading: false,
      errorCodeList: [],
      queryForm: {
        boutiqueBrandName: null,
        seasonCode: null,
        coFlag: null,
        designerId: null,
        colorcode: null,
        stock: null,
        codeIds: null,
        draftTag: null,
        orderBy: null,
        desc: null,
        pageNo: 1,
        pageSize: 25,
        wholesaleNo: null
      },
      total: 0,
      tableData: [],
      selections: [],
      dialogVisible: false,
      drawer: false,
      drawerSimilar: {},
      drawerSimilarList: [],
      codeList: [],
      dialogVisibleSame: false,
      wholesaleNo: null,
      params: {}
    };
  },
  computed: mapState({
    showIsCo: (state) => state.userinfo.coFlag === 1,
    showNewD: (state) => state.userinfo.vendorType === 7,
    vendorType: (state) => state.userinfo.vendorType,
  }),
  created() {
    getErrorCodeList().then((res) => {
      this.errorCodeList = res.data;
    });
    this.getTableData();
    getUserInfo().then((res) => {
      if (res && res.data) {
        if(res.data.vendorType === 7){
          this.getWholesaleValidList()
        }
      }
    });
  },
  methods: {
    getWholesaleValidList() {
      getWholesaleValidList().then((res) => {
        this.codeList = res.data || [];
      });
    },
    search() {
      this.queryForm.pageNo = 1;
      this.getTableData();
    },
    reset() {
      this.queryForm.boutiqueBrandName = null;
      this.queryForm.seasonCode = null;
      this.queryForm.coFlag = null;
      this.queryForm.designerId = null;
      this.queryForm.colorcode = null;
      this.queryForm.stock = null;
      this.queryForm.codeIds = null;
      this.queryForm.draftTag = null;
      this.queryForm.pageNo = 1;
      this.queryForm.wholesaleNo = null;
      this.getTableData();
    },
    getTableData() {
      this.loading = true;
      getDraftsList({
        ...this.queryForm,
        codeIds: this.queryForm.codeIds ? [this.queryForm.codeIds] : null,
      }).then((res) => {
        this.total = res.data.total || 0;
        this.tableData = (res.data.list || []).map((item) => {
          const temp = [];
          if (item.category1Name) {
            temp.push(item.category1Name);
          }
          if (item.category2Name) {
            temp.push(item.category2Name);
          }
          if (item.category3Name) {
            temp.push(item.category3Name);
          }
          item.categoryPath = temp.join('>');
          item.loadStatus = 0;
          item.similar = null;
          item.similarList = [];
          item.errorCodeFold = true;
          item.errorCodeList = [];
          return item;
        });
        this.loading = false;
      });
    },
    handleSizeChange(pageSize) {
      this.queryForm.pageSize = pageSize;
      this.getTableData();
    },
    handleCurrentChange(pageNo) {
      this.queryForm.pageNo = pageNo;
      this.getTableData();
    },
    tableSelectionChange(selections) {
      this.selections = selections;
    },
    sortChange({ prop, order }) {
      if (order) {
        this.queryForm.orderBy = prop;
        this.queryForm.desc = order === 'descending';
      } else {
        this.queryForm.orderBy = null;
        this.queryForm.desc = null;
      }
      this.getTableData();
    },
    handleBatchDelete(selections) {
      if (selections.length === 0) {
        return;
      }
      this.$confirm(this.$t('product.sureDelete'), this.$t('common.warning'), {
        confirmButtonText: this.$t('common.ok'),
        cancelButtonText: this.$t('common.cancel'),
        type: 'warning',
      }).then(() => {
        const ids = selections.map((item) => item.boutiqueProductId);
        deleteDraft(ids).then((res) => {
          if (res.success) {
            this.$message.success(this.$t('error.successful'));
            this.getTableData();
          } else {
            this.$message.error(res.message || this.$t('error.systemerror'));
          }
        });
      });
    },
    handleExport(command) {
      switch (command) {
        case 'comma':
          this.exportCSVOrExcel(1);
          break;
        case 'semicolon':
          this.exportCSVOrExcel(2);
          break;
        case 'excel':
          this.exportCSVOrExcel(1, 2);
          break;
        default:
          break;
      }
    },
    exportCSVOrExcel(eventType, type) {
      this.loading = true;
      let request = exportDraftsProduct;
      if (type === 2) {
        request = exportProductExcel;
      }
      request(eventType, {
        ...this.queryForm,
        codeIds: this.queryForm.codeIds ? [this.queryForm.codeIds] : null,
      }).then((res) => {
        this.loading = false;
        if (res.success) {
          this.dialogVisible = true;
        } else {
          if (res.code === 1 || res.code === '1') {
            res.message = this.$t('product.exportingLater');
          }
          this.$message.error(res.message || this.$t('error.systemerror'));
        }
      });
    },
    loadErrorCodeList(scope) {
      if (this.tableData[scope.$index].errorCodeList.length > 0) {
        this.tableData[scope.$index].errorCodeFold = false;
        return;
      }
      queryBoutiqueErrorByBoutiqueProductId({
        boutiqueProductId: scope.row.boutiqueProductId,
      }).then((res) => {
        if (res.success) {
          this.tableData[scope.$index].errorCodeFold = false;
          this.tableData[scope.$index].errorCodeList = res.data || [];
        } else {
          this.$message.error(res.message || this.$t('error.systemerror'));
        }
      });
    },
    openEdit(row) {
      window.open(`${this.$router.options.base}/product/edit/${row.boutiqueProductId}?sketch=1`);
    },
    loadSimilar(scope) {
      this.tableData[scope.$index].loadStatus = 1;
      searchLikeProductList({
        boutiqueProductId: scope.row.boutiqueProductId,
      })
        .then((res) => {
          if (res.success) {
            const data = (res.data || []).map((item) => {
              const coverImg = JSON.parse(item.coverImg);
              return {
                ...item,
                coverImg,
                img: coverImg[0] || null,
              };
            });
            this.tableData[scope.$index].similarList = data;
            if (data[0]) {
              this.tableData[scope.$index].similar = data[0];
            }
            this.tableData[scope.$index].loadStatus = 2;
          } else {
            this.tableData[scope.$index].loadStatus = 0;
            this.$message.error(res.message || this.$t('error.systemerror'));
          }
        })
        .catch(() => {
          this.tableData[scope.$index].loadStatus = 0;
        });
    },
    showDrawer(scope) {
      this.drawerSimilar = scope.row;
      this.drawerSimilarList = scope.row.similarList;
      this.currIndex = scope.$index;
      this.drawer = true;
    },
    similarCreateProduct(item) {
      if (this.repeatSubmit) {
        return;
      }
      if (this.vendorType == 7){
        this.dialogVisibleSame = true;
        this.wholesaleNo = null;
        this.params = {
          spuId: item.spuId,
          boutiqueProductId: item.boutiqueProductId,
        }
        return;
      }
      this.repeatSubmit = true;
      similarCreateProduct({
        spuId: item.spuId,
        boutiqueProductId: item.boutiqueProductId,
      })
        .then((res) => {
          if (res.success) {
            this.tableData.splice(this.currIndex, 1);
            this.drawer = false;
            this.$message.success(this.$t('error.successful'));
          } else {
            this.$message.error(res.message || this.$t('error.systemerror'));
          }
          this.repeatSubmit = false;
        })
        .catch(() => {
          this.repeatSubmit = false;
        });
    },
    similarCreateProductNew() {
      if (!this.wholesaleNo){
        this.$message.error(this.$t('product.wholesaleNo'));
        return;
      }
      if (this.repeatSubmit) {
        return;
      }
      this.repeatSubmit = true;
      similarCreateProduct({
        ...this.params,
        wholesaleNo: this.wholesaleNo
      })
        .then((res) => {
          if (res.success) {
            this.tableData.splice(this.currIndex, 1);
            this.drawer = false;
            this.dialogVisibleSame = false;
            this.$message.success(this.$t('error.successful'));
          } else {
            this.$message.error(res.message || this.$t('error.systemerror'));
          }
          this.repeatSubmit = false;
        })
        .catch(() => {
          this.repeatSubmit = false;
        });
    },
    foldErrorCodeList(scope) {
      this.tableData[scope.$index].errorCodeFold = true;
    },
    handleBatchProcess(selections) {
      if (selections.length === 0) {
        return;
      }
      this.$confirm(this.$t('common.whetherToContinue'), this.$t('common.warning'), {
        confirmButtonText: this.$t('common.ok'),
        cancelButtonText: this.$t('common.cancel'),
        type: 'warning',
      }).then(() => {
        const ids = selections.map((item) => item.boutiqueProductId);
        draftsProductMarkTag(ids).then((res) => {
          if (res.success) {
            this.$message.success(this.$t('error.successful'));
            this.getTableData();
          } else {
            this.$message.error(res.message || this.$t('error.systemerror'));
          }
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.label {
  font-size: 12px;
  font-weight: 300;
}
.product-info {
  position: relative;
  padding-left: 90px;
  min-height: 98px;

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 80px;
    max-height: 98px;
    border-radius: 5px;
  }
}
.drawer-container {
  padding-left: 20px;
}
.product-list {
  margin-top: 10px;
  border-top: 1px solid #ebeef5;
}
.product-item {
  display: flex;
  padding: 20px 0;
  font-size: 14px;
  border-bottom: 1px solid #ebeef5;

  .product-imgs {
    display: flex;
    margin-right: 10px;

    img {
      width: 140px;
      margin-right: 10px;
      border-radius: 5px;
    }
  }
  p {
    margin-bottom: 10px;
  }
}
.product-cell {
  position: relative;
  // z-index: 10;

  .signet {
    position: absolute;
    top: 20px;
    left: 20px;
    padding: 2px 12px;
    color: #999;
    font-weight: 600;
    border: 2px solid #999;
    border-radius: 2px;
    transform: rotate(-25deg);
    opacity: 0.4;
    pointer-events: none;
    // z-index: 9;

    &::after {
      content: "";
      position: absolute;
      top: 2px;
      right: 2px;
      bottom: 2px;
      left: 2px;
      border: 1px solid #999;
    }
    &.light {
      color: red;
      border-color: red;

      &::after {
        border-color: red;
      }
    }
  }
}
</style>
